import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import type { Currency } from '@vy/types';
import { getFormattedNumberWithCurrencySymbol } from '../../../../../services/currency-symbol-mapper';
import {
  ReportChartTooltipFrame,
  ReportChartTooltipRow,
} from '../../../reports-components/ReportChartTooltip';
import { CHART_COLORS, CHART_SERIES_IDS, SHOW_NET_NEW_MRR_LINE } from './recurring-revenue-report-chart-consts';
import type { WaterfallChartData } from '../../hooks/use-recurring-revenue-report-chart-data';

const TIMESERIES_ITEMS = [
  { id: CHART_SERIES_IDS.Mrr, label: 'MRR', color: CHART_COLORS.mrr },
  { id: CHART_SERIES_IDS.New, label: 'New', color: CHART_COLORS.new },
  { id: CHART_SERIES_IDS.Expansion, label: 'Expansion', color: CHART_COLORS.expansion },
  { id: CHART_SERIES_IDS.Reactivation, label: 'Reactivation', color: CHART_COLORS.reactivation },
  { id: CHART_SERIES_IDS.Contraction, label: 'Contraction', color: CHART_COLORS.contraction, negate: true },
  { id: CHART_SERIES_IDS.Churn, label: 'Churn', color: CHART_COLORS.churn, negate: true },
];

const LINE_ITEMS = [
  { id: 'netNewMrr', label: 'Net New', color: '#1B3D55' },
];

type RecurringRevenueReportChartTooltipProps = {
  currency: Currency;
  mode: 'timeseries' | 'waterfall';
  waterfall?: WaterfallChartData;
};

export const RecurringRevenueReportChartTooltip: React.FC<RecurringRevenueReportChartTooltipProps> = ({
  currency,
  mode,
  waterfall,
}) => {
  const tooltipData = useAxesTooltip()?.[0];

  const formatValue = (value: number): string =>
    getFormattedNumberWithCurrencySymbol(currency, value, 0);

  const renderContent = () => {
    if (!tooltipData) return null;

    const { axisValue, dataIndex, seriesItems } = tooltipData;

    if (mode === 'waterfall' && waterfall) {
      return <WaterfallTooltipContent waterfall={waterfall} dataIndex={dataIndex} formatValue={formatValue} />;
    }

    const seriesMap = new Map(seriesItems.map((item) => [item.seriesId, item]));

    const getSeriesValue = (id: string, negate?: boolean): string => {
      const item = seriesMap.get(id);
      if (!item || typeof item.value !== 'number') return '--';
      // Losses are fed to the chart as positive magnitudes so they stack above
      // the MRR base. In the tooltip we still show them with a minus sign to
      // preserve accounting semantics.
      const signed = negate ? -Math.abs(item.value) : item.value;
      return formatValue(signed);
    };

    return (
      <ReportChartTooltipFrame title={String(axisValue)}>
        {TIMESERIES_ITEMS.map(({ id, label, color, negate }) => (
          <ReportChartTooltipRow
            key={id}
            color={color}
            label={label}
            formattedValue={getSeriesValue(id, negate)}
          />
        ))}
        {SHOW_NET_NEW_MRR_LINE &&
          LINE_ITEMS.map(({ id, label, color }) => (
            <ReportChartTooltipRow
              key={id}
              color={color}
              label={label}
              formattedValue={getSeriesValue(id)}
            />
          ))}
      </ReportChartTooltipFrame>
    );
  };

  return <ChartsTooltipContainer trigger="axis">{renderContent()}</ChartsTooltipContainer>;
};

type WaterfallTooltipContentProps = {
  waterfall: WaterfallChartData;
  dataIndex: number;
  formatValue: (value: number) => string;
};

const WaterfallTooltipContent: React.FC<WaterfallTooltipContentProps> = ({
  waterfall,
  dataIndex,
  formatValue,
}) => {
  const row = WATERFALL_TOOLTIP_ROWS[dataIndex];
  if (!row) return null;

  const rawValue = row.getValue(waterfall);
  const value = row.showAbsolute ? Math.abs(rawValue) : rawValue;

  return (
    <ReportChartTooltipFrame title={row.label}>
      <ReportChartTooltipRow color={row.color} label={row.label} formattedValue={formatValue(value)} />
    </ReportChartTooltipFrame>
  );
};

const WATERFALL_TOOLTIP_ROWS: {
  label: string;
  color: string;
  showAbsolute: boolean;
  getValue: (w: WaterfallChartData) => number;
}[] = [
  { label: 'Beginning', color: CHART_COLORS.beginning, showAbsolute: false, getValue: (w) => w.beginning },
  { label: 'New', color: CHART_COLORS.new, showAbsolute: false, getValue: (w) => w.new },
  { label: 'Expansion', color: CHART_COLORS.expansion, showAbsolute: false, getValue: (w) => w.expansion },
  { label: 'Reactivation', color: CHART_COLORS.reactivation, showAbsolute: false, getValue: (w) => w.reactivation },
  { label: 'Contraction', color: CHART_COLORS.contraction, showAbsolute: true, getValue: (w) => w.contraction },
  { label: 'Churn', color: CHART_COLORS.churn, showAbsolute: true, getValue: (w) => w.churn },
  { label: 'Ending', color: CHART_COLORS.ending, showAbsolute: false, getValue: (w) => w.ending },
];
